import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as HealthyIcon,
  Warning as UnhealthyIcon,
  RemoveCircleOutline as EmptyIcon,
  Router as SegmentIcon,
  Devices as DevicesIcon,
  Computer as AgentIcon,
  Search as SearchIcon,
  Scanner as ScanIcon,
} from '@mui/icons-material';
import { useTenant } from '../../libs/contexts/TenantContext';
import { get, post } from '../../api/apiClient';
import { toast } from 'react-toastify';

const SegmentHealthPage = ({ tenantId: tenantIdProp } = {}) => {
  const { selectedTenant: contextTenant } = useTenant();
  const tenantId = tenantIdProp || contextTenant?.id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [expandedSegments, setExpandedSegments] = useState({});

  const fetchSegmentHealth = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await get(`/api/v2/segments/health?tenantId=${tenantId}`);

      if (response?.success) {
        setHealthData(response.data);
      } else {
        setError(response?.error || 'Failed to fetch segment health');
      }
    } catch (err) {
      console.error('Error fetching segment health:', err);
      setError(err.message || 'Failed to fetch segment health');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchSegmentHealth();
  }, [fetchSegmentHealth]);

  const toggleSegmentExpand = (segment) => {
    setExpandedSegments((prev) => ({
      ...prev,
      [segment]: !prev[segment],
    }));
  };

  const handleFindAgents = async (segmentId) => {
    try {
      const encodedSegment = encodeURIComponent(segmentId);
      const response = await get(`/api/v2/segments/${encodedSegment}/agents?tenantId=${tenantId}`);
      
      if (response?.success) {
        const agents = response.data?.agents || [];
        if (agents.length === 0) {
          toast.info(`No agents found in segment ${segmentId}`);
        } else {
          toast.success(`Found ${agents.length} agent(s) in segment ${segmentId}`);
        }
        // Refresh health data to show updated agent list
        await fetchSegmentHealth();
      } else {
        toast.error(response?.error || 'Failed to find agents');
      }
    } catch (err) {
      console.error('Error finding agents:', err);
      toast.error('Failed to find agents in segment');
    }
  };

  const handleScanDevices = async (segmentId, agentIds) => {
    if (!agentIds || agentIds.length === 0) {
      toast.error('No online agents available in this segment to perform scan');
      return;
    }

    try {
      // Trigger device intelligence scan for all devices in the tenant
      const response = await post('/api/v2/device-intelligence/scan/start', {
        tenantId: tenantId,
        all: true,
      });

      if (response?.taskId) {
        toast.success(`Device scan started (Task ID: ${response.taskId}). Scanning ${response.total || 0} devices.`);
        // Refresh segment health after a delay to show updated status
        setTimeout(() => fetchSegmentHealth(), 5000);
      } else {
        toast.error('Failed to start device scan');
      }
    } catch (err) {
      console.error('Error starting device scan:', err);
      toast.error(err.message || 'Failed to start device scan');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <HealthyIcon sx={{ color: 'success.main' }} />;
      case 'unhealthy':
        return <UnhealthyIcon sx={{ color: 'warning.main' }} />;
      case 'empty':
        return <EmptyIcon sx={{ color: 'text.disabled' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      healthy: 'success',
      unhealthy: 'warning',
      empty: 'default',
    };
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colors[status] || 'default'}
        size="small"
      />
    );
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a tenant to view segment health.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <SegmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Network Segment Health
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchSegmentHealth} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : healthData ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Segments
                  </Typography>
                  <Typography variant="h4">{healthData.totalSegments}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Healthy
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {healthData.healthyCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Unhealthy (Dark)
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {healthData.unhealthyCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Online Agents
                  </Typography>
                  <Typography variant="h4">{healthData.totalOnlineAgents}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Unhealthy Segments Alert */}
          {healthData.unhealthyCount > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>{healthData.unhealthyCount} dark segment(s)</strong> have devices but no online
              agents. Consider installing an agent in:{' '}
              {healthData.unhealthySegments.join(', ')}
            </Alert>
          )}

          {/* Segments Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50}></TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">
                    <DevicesIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Devices
                  </TableCell>
                  <TableCell align="center">
                    <AgentIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Online Agents
                  </TableCell>
                  <TableCell align="center">Offline Agents</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {healthData.segments?.map((segment) => (
                  <React.Fragment key={segment.segment}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleSegmentExpand(segment.segment)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedSegments[segment.segment] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(segment.status)}
                          <Typography variant="body1" fontFamily="monospace">
                            {segment.segment}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(segment.status)}</TableCell>
                      <TableCell align="center">{segment.deviceCount}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={segment.onlineAgentCount}
                          color={segment.onlineAgentCount > 0 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {segment.offlineAgentCount > 0 ? (
                          <Chip label={segment.offlineAgentCount} color="default" size="small" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedSegments[segment.segment]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2">
                                Agents in this segment:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Find agents on this network segment">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFindAgents(segment.segment);
                                    }}
                                  >
                                    <SearchIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Scan for Algo devices on this segment">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleScanDevices(
                                        segment.segment,
                                        segment.agents?.filter(a => a.is_online).map(a => a.id)
                                      );
                                    }}
                                    disabled={segment.onlineAgentCount === 0}
                                  >
                                    <ScanIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            {segment.agents?.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {segment.agents.map((agent) => (
                                  <Chip
                                    key={agent.id}
                                    label={agent.id}
                                    color={agent.is_online ? 'success' : 'default'}
                                    variant={agent.is_online ? 'filled' : 'outlined'}
                                    size="small"
                                    icon={<AgentIcon />}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No agents assigned to this segment
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {(!healthData.segments || healthData.segments.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No segments found. Run a network scan to discover devices and segments.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  );
};

export default SegmentHealthPage;
